import React, { useState, useEffect } from 'react';
import { Lock, CheckCircle, AlertCircle, Loader2, Eye, EyeOff } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../config/supabase';

const ResetPasswordPage: React.FC = () => {
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [mostrarConfirmar, setMostrarConfirmar] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [verificando, setVerificando] = useState(true);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Verificar se há um hash na URL (Supabase envia o token no hash)
    const verificarSessao = async () => {
      try {
        // Verificar se há sessão ativa (após clicar no link do email)
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Erro ao verificar sessão:', error);
          setErro('Link inválido ou expirado. Por favor, solicite um novo link de redefinição.');
          setVerificando(false);
          return;
        }

        if (!session) {
          // Tentar verificar o hash na URL
          const hash = window.location.hash;
          if (hash && hash.includes('access_token')) {
            // O Supabase processará automaticamente o hash e criará uma sessão
            setVerificando(true);
            // Aguardar um pouco para o Supabase processar
            setTimeout(async () => {
              const { data: { session: newSession } } = await supabase.auth.getSession();
              if (!newSession) {
                setErro('Link inválido ou expirado. Por favor, solicite um novo link de redefinição.');
              }
              setVerificando(false);
            }, 1000);
          } else {
            setErro('Link inválido ou expirado. Por favor, solicite um novo link de redefinição.');
            setVerificando(false);
          }
        } else {
          setVerificando(false);
        }
      } catch (err) {
        console.error('Erro ao verificar:', err);
        setErro('Erro ao verificar link. Por favor, tente novamente.');
        setVerificando(false);
      }
    };

    verificarSessao();
  }, []);

  // Validações da senha
  const validacoes = {
    minimo8: novaSenha.length >= 8,
    temMaiuscula: /[A-Z]/.test(novaSenha),
    temMinuscula: /[a-z]/.test(novaSenha),
    temNumero: /\d/.test(novaSenha),
    temEspecial: /[!@#$%^&*(),.?":{}|<>]/.test(novaSenha),
    senhasIguais: novaSenha === confirmarSenha && novaSenha.length > 0
  };

  const senhaValida = Object.values(validacoes).every(Boolean);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!senhaValida) {
      setErro('Por favor, atenda a todos os requisitos de senha');
      return;
    }

    setLoading(true);
    setErro(null);

    try {
      // Atualizar a senha do usuário
      const { error: updateError } = await supabase.auth.updateUser({
        password: novaSenha
      });

      if (updateError) {
        throw updateError;
      }

      setSuccess(true);
      
      // Redirecionar para login após 2 segundos
      setTimeout(() => {
        navigate('/');
      }, 2000);
      
    } catch (err) {
      console.error('Erro ao atualizar senha:', err);
      setErro(err instanceof Error ? err.message : 'Erro ao atualizar senha');
    } finally {
      setLoading(false);
    }
  };

  const ValidacaoItem = ({ valida, texto }: { valida: boolean; texto: string }) => (
    <div className={`flex items-center gap-2 text-xs ${valida ? 'text-green-600' : 'text-gray-400'}`}>
      {valida ? (
        <CheckCircle className="w-4 h-4" />
      ) : (
        <div className="w-4 h-4 rounded-full border-2 border-gray-300" />
      )}
      <span>{texto}</span>
    </div>
  );

  if (verificando) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Verificando link de redefinição...</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Senha Atualizada!</h2>
          <p className="text-gray-600 mb-4">
            Sua senha foi atualizada com sucesso. Redirecionando para o login...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <img 
              src="/logo/logoEhsFacil.png" 
              alt="CIPA FÁCIL EHS Logo" 
              className="w-16 h-16 object-contain"
            />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            Redefinir Senha
          </h2>
          <p className="text-gray-600 text-sm">
            Digite sua nova senha abaixo
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-2xl p-8 space-y-6">
          {erro && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-red-700 text-sm font-medium">Erro</p>
                <p className="text-red-600 text-sm mt-1">{erro}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="novaSenha" className="block text-sm font-medium text-gray-700 mb-2">
                Nova Senha
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="novaSenha"
                  name="novaSenha"
                  type={mostrarSenha ? 'text' : 'password'}
                  required
                  value={novaSenha}
                  onChange={(e) => setNovaSenha(e.target.value)}
                  className="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="••••••••"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setMostrarSenha(!mostrarSenha)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  {mostrarSenha ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="confirmarSenha" className="block text-sm font-medium text-gray-700 mb-2">
                Confirmar Nova Senha
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="confirmarSenha"
                  name="confirmarSenha"
                  type={mostrarConfirmar ? 'text' : 'password'}
                  required
                  value={confirmarSenha}
                  onChange={(e) => setConfirmarSenha(e.target.value)}
                  className="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="••••••••"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setMostrarConfirmar(!mostrarConfirmar)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  {mostrarConfirmar ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg space-y-2">
              <p className="text-sm font-medium text-gray-700 mb-2">Requisitos da senha:</p>
              <ValidacaoItem valida={validacoes.minimo8} texto="Mínimo de 8 caracteres" />
              <ValidacaoItem valida={validacoes.temMaiuscula} texto="Pelo menos uma letra maiúscula" />
              <ValidacaoItem valida={validacoes.temMinuscula} texto="Pelo menos uma letra minúscula" />
              <ValidacaoItem valida={validacoes.temNumero} texto="Pelo menos um número" />
              <ValidacaoItem valida={validacoes.temEspecial} texto="Pelo menos um caractere especial" />
              <ValidacaoItem valida={validacoes.senhasIguais} texto="As senhas devem ser iguais" />
            </div>

            <button
              type="submit"
              disabled={loading || !senhaValida}
              className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Atualizando...
                </>
              ) : (
                'Atualizar Senha'
              )}
            </button>
          </form>

          <div className="text-center pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors"
            >
              Voltar para o login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;

